import moment from "moment";
import {
  DropdownItem,
  GroupByYearMonthWeek,
  InvoiceGroup,
  OrgUserGroupByRole,
  SuperAdminDashboardResponse,
  WashesGroup,
} from "../types";
export type DashboardPeriod = "daily" | "weekly" | "monthly";
export const getNumberOfWashesByDate = (
  washData: WashesGroup[],
  date: string,
): number => {
  return washData.find((wash) => wash.key === date)?.numberOfWashes || 0;
};

export const getNumberOfWashesByDateRange = (
  washData: WashesGroup[],
  startDate: string,
  endDate: string,
): number => {
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).endOf("day");

  return washData
    .filter((wash) => {
      const washDate = moment(wash.key).startOf("day");
      return washDate.isSameOrAfter(start) && washDate.isSameOrBefore(end);
    })
    .reduce((total, wash) => total + wash.numberOfWashes, 0);
};

export const getInvoiceAmountByDate = (
  invoiceData: InvoiceGroup[],
  date: string,
): number => {
  const invoice = invoiceData.find((invoice) => invoice.key === date);
  return invoice ? invoice.invoiceAmount : 0;
};

export const getInvoiceAmountByDateRange = (
  invoiceData: InvoiceGroup[],
  startDate: string,
  endDate: string,
): number => {
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).endOf("day");
  return invoiceData
    .filter((invoice) => {
      const invoiceDate = moment(invoice.key).startOf("day");
      return (
        invoiceDate.isSameOrAfter(start) && invoiceDate.isSameOrBefore(end)
      );
    })
    .reduce((total, invoice) => total + invoice.invoiceAmount, 0);
};

export const getTotalNumberOfUsers = (
  usersData: OrgUserGroupByRole[],
): number => {
  return usersData.reduce(
    (total, userGroup) => total + userGroup.numberOfUsers,
    0,
  );
};

export const getProgressPercent = (
  currentPeriodValue: number,
  previousPeriodValue: number,
): number => {
  let progressPercent = 0;

  if (previousPeriodValue > 0) {
    progressPercent =
      ((currentPeriodValue - previousPeriodValue) / previousPeriodValue) * 100;
  }

  return progressPercent;
};

export const aggregateWashesByMonth = (
  washData: GroupByYearMonthWeek,
  year: string,
) => {
  const result: { month: string; totalWashes: number }[] = [];

  if (washData[year]) {
    const months = washData[year];
    for (const month in months) {
      let totalWashesForMonth = 0;

      for (const week in months[month]) {
        totalWashesForMonth += months[month][week];
      }

      result.push({
        month,
        totalWashes: totalWashesForMonth,
      });
    }
  }

  return result;
};

export const aggregateWashesByMonthAndWeek = (
  washData: GroupByYearMonthWeek,
  year: string,
) => {
  const data = washData[year];
  const outputData: Array<{ [key: string]: number | string }> = [];

  for (const month in data) {
    const weekTotals: { [key: string]: number } = {
      week1: 0,
      week2: 0,
      week3: 0,
      week4: 0,
      week5: 0,
    };

    for (const week in data[month]) {
      const weekNumber = parseInt(week) - 27;
      if (weekNumber >= 1 && weekNumber <= 5) {
        weekTotals[`week${weekNumber}`] += data[month][week];
      }
    }

    outputData.push({
      ...weekTotals,
      month: month,
    });
  }
  return outputData;
};

export const getWashesCountInDateRange = (
  washesGroupedByYearMonthAndWeek: GroupByYearMonthWeek,
  startDate: string,
  endDate: string,
): number => {
  const start = moment(startDate);
  const end = moment(endDate);
  let totalWashes = 0;

  for (const year in washesGroupedByYearMonthAndWeek) {
    const yearData = washesGroupedByYearMonthAndWeek[year];

    for (const month in yearData) {
      const monthData = yearData[month];

      const monthStartDate = moment(`${year}-${month}-01`, [
        "YYYY-MMMM-DD",
        "YYYY-MM-DD",
      ]);
      const monthEndDate = monthStartDate.clone().endOf("month");

      if (
        monthEndDate.isSameOrAfter(start) &&
        monthStartDate.isSameOrBefore(end)
      ) {
        for (const week in monthData) {
          const weekData = monthData[week];
          totalWashes += weekData;
        }
      }
    }
  }

  return totalWashes;
};

export const getInvoiceAmountByKey = (
  data: InvoiceGroup[],
  key: string,
): number => {
  const item = data.find((entry) => entry.key === key);
  return item ? item.invoiceAmount : 0;
};

export const formatCurrency = (
  amount: number,
  currency: string = "USD",
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

export const getInvoiceCountByDateRange = (
  invoiceData: InvoiceGroup[],
  startDate: string,
  endDate: string,
): number => {
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).endOf("day");

  return invoiceData
    .filter((invoice) => {
      const invoiceDate = moment(invoice.key).startOf("day");
      return (
        invoiceDate.isSameOrAfter(start) && invoiceDate.isSameOrBefore(end)
      );
    })
    .reduce((total, invoice) => total + invoice.numberOfInvoices, 0);
};

export const buildRevenueTrendData = (
  dashboardItems?: SuperAdminDashboardResponse,
) => {
  const invoiceDateData = dashboardItems?.invoicesGroupedByDate ?? [];
  const washDateData = dashboardItems?.washesGroupedByDate ?? [];

  const allDates = Array.from(
    new Set([
      ...invoiceDateData.map((item) => item.key),
      ...washDateData.map((item) => item.key),
    ]),
  );

  return allDates
    .sort((a, b) => moment(a).diff(moment(b)))
    .map((date) => ({
      month: moment(date).format("MMM D"),
      washes:
        washDateData.find((wash) => wash.key === date)?.numberOfWashes ?? 0,
      revenue:
        invoiceDateData.find((inv) => inv.key === date)?.invoiceAmount ?? 0,
    }));
};

export const getTotalAccounts = (
  dashboardItems?: SuperAdminDashboardResponse,
): number => {
  return (
    dashboardItems?.accountsGroupedByStatus.reduce(
      (sum, account) => sum + account.numberOfAccounts,
      0,
    ) ?? 0
  );
};
export const getSelectedOrgAccountCount = (
  dashboardItems: SuperAdminDashboardResponse | undefined,
  selectedOrgId: number,
  selectedOrgName: DropdownItem["key"] | undefined,
) => {
  if (selectedOrgId === 0) return 0;

  return (
    dashboardItems?.accountsGroupedByStatus
      ?.filter((account) => account.orgName === selectedOrgName)
      .reduce((total, account) => total + (account.numberOfAccounts || 0), 0) ??
    0
  );
};

export const getCurrentAvgWashRate = (
  revenue: number,
  washes: number,
): number => {
  return washes > 0 ? revenue / washes : 0;
};

const sumWashes = (items?: SuperAdminDashboardResponse) =>
  items?.totalWashesInPeriod ?? 0;

const sumInvoices = (items?: SuperAdminDashboardResponse) => {
  return {
    revenue: items?.totalInvoiceAmountInPeriod ?? 0,
    count: items?.totalInvoicesInPeriod ?? 0,
  };
};

type SuperAdminMetricsParams = {
  dashboardItems?: SuperAdminDashboardResponse;
  previousDashboardItems?: SuperAdminDashboardResponse;
  period: DashboardPeriod;
};

export const getSuperAdminDashboardMetrics = ({
  dashboardItems,
  previousDashboardItems,
  period,
}: SuperAdminMetricsParams) => {
  const currentWashes = sumWashes(dashboardItems);
  const { revenue: currentRevenue, count: currentInvoiceCount } =
    sumInvoices(dashboardItems);

  const previousWashes = sumWashes(previousDashboardItems);
  const { revenue: previousRevenue, count: previousInvoiceCount } = sumInvoices(
    previousDashboardItems,
  );

  const avgInvoiceAmount =
    currentInvoiceCount > 0 ? currentRevenue / currentInvoiceCount : 0;

  const avgVehiclesPerDay =
    period === "daily"
      ? currentWashes
      : period === "weekly"
        ? currentWashes / 7
        : currentWashes / new Date().getDate();

  const previousAvgWashRate =
    previousWashes > 0 ? previousRevenue / previousWashes : 0;

  const previousPeriodLabel =
    period === "daily"
      ? "Yesterday"
      : period === "weekly"
        ? "Last Week"
        : "Last Month";

  const washesGrowth =
    period === "daily"
      ? undefined
      : `${getProgressPercent(currentWashes, previousWashes).toFixed(1)}%`;

  const revenueGrowth =
    period === "daily"
      ? undefined
      : `${getProgressPercent(currentRevenue, previousRevenue).toFixed(1)}%`;

  return {
    avgInvoiceAmount,
    avgVehiclesPerDay,
    currentInvoiceCount,
    currentRevenue,
    currentWashes,
    previousAvgWashRate,
    previousInvoiceCount,
    previousPeriodLabel,
    previousRevenue,
    previousWashes,
    revenueGrowth,
    washesGrowth,
  };
};

type OrgAdminMetricsParams = SuperAdminMetricsParams;

export const buildOrgAdminMetrics = ({
  dashboardItems,
  previousDashboardItems,
  period,
}: OrgAdminMetricsParams) => {
  const metrics = getSuperAdminDashboardMetrics({
    dashboardItems,
    previousDashboardItems,
    period,
  });

  const totalAccounts = getTotalAccounts(dashboardItems);

  const totalUsers = getTotalNumberOfUsers(
    dashboardItems?.usersGroupedByRole ?? [],
  );

  const currentAvgWashRate = getCurrentAvgWashRate(
    metrics.currentRevenue,
    metrics.currentWashes,
  );

  return {
    ...metrics,
    currentAvgWashRate,
    totalAccounts,
    totalUsers,
  };
};
